import type { QueryClient } from '@tanstack/react-query'
import type { usePageControls } from '~/api/hooks/useCharts'
import type { KpiMetadataItem, Controls } from '~/api/endpoints/getControls'

/** "<display_root> (<display_value_type>)" when a value_type is present, else bare root. */
export function labelFromItem(item: KpiMetadataItem): string {
  const root = item.display_root ?? item.kpi_id
  return item.display_value_type ? `${root} (${item.display_value_type})` : root
}

/**
 * Nice display label for a kpi_id from controls.kpi_metadata.items.
 * Falls back to the raw kpi_id if not found. Access via .kpi_metadata.items — NOT .kpi_metadata.
 */
export function labelFor(controls: ReturnType<typeof usePageControls>, kpiId: string): string {
  const item = controls.data?.kpi_metadata?.items?.find((m) => m.kpi_id === kpiId)
  return item ? labelFromItem(item) : kpiId
}

/** FU-31: best-effort display label for a root the CURRENT page cannot resolve — scans every
 *  cached get_controls entry (other pages the session already visited). Quoted raw token when
 *  nothing is cached; the NOTICE may name a raw id, the selector never renders one again. */
export function labelForRootFromCache(qc: QueryClient, root: string): string {
  for (const [, data] of qc.getQueriesData<Controls>({ queryKey: ['get_controls'] })) {
    const item = data?.kpi_metadata?.items?.find((m) => m.kpi_root === root)
    if (item) return item.display_root ?? root
  }
  return `"${root}"`
}
